import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Trophy, Share2, Music, Headphones, Users, Sparkles, Star,
  Clock, CheckCircle, XCircle, Send, ChevronRight, Filter, Crown,
} from "lucide-react";

import { toast } from "sonner";
import { ArtistCreateFab } from "@/components/ArtistCreateFab";

const TYPE_ICONS = {
  share: Share2,
  create: Music,
  listen: Headphones,
  attend: Users,
  invite: Users,
  custom: Sparkles,
};

const TYPE_LABELS = {
  share: "Partager",
  create: "Creer",
  listen: "Ecouter",
  attend: "Participer",
  invite: "Inviter",
  custom: "Autre",
};

export default function ChallengesPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [proofText, setProofText] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [filterType, setFilterType] = useState("all");

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await api.get("/challenges", { params: { active_only: tab === "active" } });
      setChallenges(res.data.challenges);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, tab]);

  useEffect(() => {
    setLoading(true);
    fetchChallenges();
  }, [fetchChallenges]);

  const handleSubmit = async (challengeId) => {
    if (!proofText.trim() && !proofUrl.trim()) return;
    try {
      await api.post(`/challenges/${challengeId}/submit`, {
        proof_text: proofText,
        proof_url: proofUrl,
      });
      setSubmitting(null);
      setProofText("");
      setProofUrl("");
      fetchChallenges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const filtered = challenges.filter(
    (c) => filterType === "all" || c.challenge_type === filterType
  );

  return (
    <div className="py-6 space-y-5" data-testid="challenges-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-2xl text-foreground">Defis</h1>
          <p className="text-sm text-muted-foreground mt-1">Complete des defis pour gagner des credits</p>
        </div>
        <button
          onClick={() => navigate("/champions")}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/20 text-[#FF6B35] text-xs font-bold hover:bg-[#FF6B35]/20 transition-all"
          data-testid="champions-link-btn"
        >
          <Crown className="w-3.5 h-3.5" />
          Champions
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-foreground/5 rounded-full p-1 border border-foreground/10">
        {[
          { key: "active", label: "En cours" },
          { key: "all", label: "Tous" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key === "all" ? (() => { setTab("all"); return; })() || "all" : "active")}
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-all ${
              (t.key === "active" && tab === "active") || (t.key === "all" && tab !== "active")
                ? "bg-[#FF6B35] text-white"
                : "text-muted-foreground"
            }`}
            data-testid={`challenges-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Type filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => setFilterType("all")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all shrink-0 ${
            filterType === "all"
              ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
              : "bg-foreground/5 border-foreground/10 text-muted-foreground"
          }`}
        >
          Tous
        </button>
        {Object.entries(TYPE_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilterType(filterType === key ? "all" : key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all shrink-0 ${
              filterType === key
                ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                : "bg-foreground/5 border-foreground/10 text-muted-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Challenges list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun defi disponible</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const Icon = TYPE_ICONS[c.challenge_type] || Star;
            const isParticipated = !!c.user_status;
            const isCompleted = c.max_completions !== -1 && c.completions_count >= c.max_completions;

            return (
              <div
                key={c.id}
                className={`bg-card border border-border rounded-3xl p-5 shadow-sm transition-all ${
                  isParticipated ? "opacity-80" : ""
                }`}
                data-testid={`challenge-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#FF6B35]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-sm text-card-foreground truncate">{c.title}</h3>
                      {isParticipated && (
                        <span className={`text-[9px] px-2 py-0.5 rounded-full shrink-0 ${
                          c.user_status === "approved" ? "bg-emerald-50 text-emerald-600" :
                          c.user_status === "rejected" ? "bg-red-50 text-red-500" :
                          "bg-yellow-50 text-yellow-600"
                        }`}>
                          {c.user_status === "approved" ? "Valide" : c.user_status === "rejected" ? "Refuse" : "En attente"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">par {c.artist_name}</p>
                    {c.description && (
                      <p className="text-xs text-card-foreground/80 mt-2 leading-relaxed">{c.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      <span className="font-mono-credits font-bold text-xs text-[#FF6B35]">
                        +{c.reward_credits} credits
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {c.participants_count} participants
                      </span>
                      {c.max_completions !== -1 && (
                        <span className="text-[10px] text-muted-foreground">
                          {c.completions_count}/{c.max_completions} valides
                        </span>
                      )}
                    </div>

                    {/* Submit form */}
                    {!isParticipated && !isCompleted && submitting === c.id && (
                      <div className="mt-3 space-y-2 p-3 bg-white rounded-2xl border border-border">
                        <textarea
                          placeholder="Decris ta participation..."
                          value={proofText}
                          onChange={(e) => setProofText(e.target.value)}
                          className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-xs text-card-foreground placeholder:text-muted-foreground resize-none h-16 focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
                          data-testid={`challenge-proof-text-${c.id}`}
                        />
                        <input
                          placeholder="Lien (optionnel)"
                          value={proofUrl}
                          onChange={(e) => setProofUrl(e.target.value)}
                          className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-xs text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
                          data-testid={`challenge-proof-url-${c.id}`}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSubmit(c.id)}
                            disabled={!proofText.trim() && !proofUrl.trim()}
                            className="flex items-center gap-1 bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                            data-testid={`challenge-submit-btn-${c.id}`}
                          >
                            <Send className="w-3 h-3" />
                            Envoyer
                          </button>
                          <button
                            onClick={() => { setSubmitting(null); setProofText(""); setProofUrl(""); }}
                            className="text-xs text-muted-foreground px-3 py-2"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Participate button */}
                    {!isParticipated && !isCompleted && submitting !== c.id && (
                      <button
                        onClick={() => setSubmitting(c.id)}
                        className="mt-3 flex items-center gap-1.5 bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform"
                        data-testid={`challenge-participate-${c.id}`}
                      >
                        <Trophy className="w-3.5 h-3.5" />
                        Participer
                      </button>
                    )}

                    {isCompleted && !isParticipated && (
                      <p className="mt-3 text-xs text-muted-foreground italic">Defi complet</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ArtistCreateFab tab="challenges" createFlag="challenge" label="Creer un defi" testId="create-challenge-fab" />
    </div>
  );
}
